'use no memo';
/**
 * Sign In/Out Hook
 *
 * Handles authentication actions: sign-in, sign-out, and user verification.
 * Integrates with Logto SDK and manages cross-tab synchronization.
 *
 * AIDEV-NOTE: 'use no memo' directive - This hook mutates refs passed as
 * props for auth state coordination. The React Compiler cannot optimize this pattern.
 */

import { useCallback, useRef } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { LogtoSignInOptions } from '@lib/logto';
import type { QueryClient } from '@tanstack/react-query';
import type { UserProfileResponse } from '@models';
import { currentUserKey } from '@lib/cache/users';
import { CrossTabAuthSync } from '@utils/crossTabAuthSync';
import { userInitiatedSignInSlot } from '@utils/crossTabAuthSync/authFlowFlags';
import {
  getAuthCallbackUrl,
  getPostLogoutRedirectUri,
  storeRedirectPath,
  shouldStoreRedirectPath,
  isAuthRoute,
  isUserCancellation,
  normalizeRoutePath,
} from '@utils/auth';
import { isNativePlatform } from '@lib/platform';
import { shouldRouteToDoor } from '@lib/magicLink/doorRouting';
import { AUTH_DOOR_PATH } from '@lib/magicLink/authLinkParams';
import { routes } from '@routes';
import { clearUserStorage, clearNonLogtoStorage, hasDraftInStorage } from '@utils/storageCleanup';
// AIDEV-NOTE [MEETBALL magic-link continuation]: id 4074244718 asked for this
// to become a dynamic `import()` (or for `continuation.ts` to be split into a
// leaf) on the grounds that a static import in an eager provider hook welds the
// whole continuation store into the entry bundle for two calls that are no-ops
// with the flag off. It stays static, MEASURED rather than argued.
//
// Measured on this branch with a production `npm run build`, taking "eager" as
// the entry script plus every chunk transitively reachable from it by a STATIC
// import (115 chunks): 1,928,009 raw / 621,201 gzipped. The same build with
// this import made dynamic: 117 chunks, 1,929,260 raw / 622,056 gzipped --
// 855 bytes gzipped BIGGER, because the split adds two chunks and removes
// nothing.
//
// WHY it removes nothing, corrected. This note used to say
// `@lib/magicLink/useScrubCapturedParams` and `createQuestionDraftStore` both
// statically import `continuation.ts`. Neither does, and neither ever did:
// `src/lib/magicLink/useScrubCapturedParams.ts` imports `react` and
// `react-router-dom` and nothing else, and `src/stores/createQuestionDraftStore.ts`
// imports `zustand` and two types. Those two names are ROLLUP CHUNK names off
// the review comment's own build output, and a chunk is not a module: a chunk
// called `createQuestionDraftStore-<hash>.js` is simply where Rollup parked a
// shared set of modules, `continuation.ts` among them.
//
// The real reason is one line up, at :32. This hook statically imports
// `@utils/storageCleanup`, and `src/utils/storageCleanup.ts:7` statically
// imports `clearContinuationOnAccountSwitch` from `@lib/magicLink/continuation`
// (932afcd2, "clear the native continuation records on an account switch too").
// So `continuation.ts` is inside this hook's own static graph whatever the
// import below does, and making that one dynamic can only add a chunk
// boundary. Two greps settle it, which is why the claim is written this way
// rather than as another bundle figure that nobody can re-check cheaply.
//
// So the fix as proposed costs bytes rather than saving them. If the eager
// reach is worth closing, the `@utils/storageCleanup` edge above is the one to
// cut -- split the sign-out and account-switch clears into a leaf that carries
// no storage adapters, no serialised queue and no `flowHash` -- and this import
// goes with it for free.
import { clearContinuationOnSignOut } from '@lib/magicLink/continuation';
import {
  clearNativePendingQuestionClaim,
  hasPendingQuestionClaimInTab,
} from '@utils/pendingQuestionClaim';
import { unregisterPushToken } from '@hooks/usePushNotifications';
import { useCreateQuestionDraftStore } from '@stores/createQuestionDraftStore';
import { useAuthStore } from '@stores/authStore';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { clearAttribution } from '@lib/attribution';
import { logger } from '@utils/logger';
import { hasAuthIntent } from '@utils/authIntent';
import { LogoutCancelledError } from '../../errors/LogoutCancelledError';
import { clearAuthState } from './clearAuthState';
import type { SignInOptions } from './authTypes';

/**
 * How long a single best-effort cleanup step may hold up sign-out.
 *
 * Two seconds is the shape of the thing being bounded, not a tuned number:
 * every step this wraps is a local storage removal or a token de-registration
 * that normally completes in single-digit milliseconds, so anything past two
 * seconds is a stall rather than slow work, and the visitor pressing Log Out
 * is owed the sign-out more than they are owed the cleanup.
 */
const SIGN_OUT_CLEANUP_TIMEOUT_MS = 2000;

/**
 * ...and the one step that is not shaped like that, so it gets its own ceiling.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: 2s is too small here: a real mobile network DELETE, not a stall.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: the DELETE needs the dying session; a retry 401s or arrives as user B.
 *
 * The rest of this note is ordinary prose, not anchor text: per `docs/aidev-guidelines.md`
 * an anchor carries the thing needing action or special attention, and explanation of what
 * the code does belongs in a regular comment. The two anchors above are the concerns; what
 * follows is why they hold.
 *
 * `unregisterPushToken` makes a NETWORK call
 * (`DELETE /api/v1/push/unregister-device` on native,
 * `DELETE /api/v1/push/unsubscribe` on web) against a 30-second axios ceiling
 * (`apiConfig.timeout`). Two seconds there is not "a stall rather than slow
 * work", it is an ordinary round trip on the mobile connections this matters
 * most on.
 *
 * IT CANNOT BE DEFERRED TO A RETRY. Both endpoints are
 * `Depends(require_authenticated_user)` and both verify ownership against
 * `current_user` (backend `push.unregister_device` and `push.unsubscribe_from_push`; by SYMBOL,
 * because the line numbers this note first carried had already moved),
 * so the only window in which the backend can be told is while the session
 * that is about to end still exists. A marker retried on the next launch would
 * 401, or -- worse, on a shared device -- reach the backend as a different
 * identity whose ownership check rejects the previous user's token. There is
 * no second chance after `logtoSignOut`.
 *
 * WHAT THIS NUMBER NO LONGER DECIDES (#1173). It used to be the whole
 * privacy trade: five seconds of Log Out against a signed-out device that kept
 * receiving the last account's pushes, because the device's own teardown ran
 * only after the DELETE. `unregisterPushToken` now STARTS the device's own
 * teardown first -- `subscription.unsubscribe()` on web, `deleteToken()` on
 * native -- and never gates it on the DELETE, so a DELETE this ceiling
 * abandons no longer leaves the device subscribed. A dead subscription or
 * token is also how the backend learns on its own: it deletes a web row on
 * the push service's 404/410 and deactivates a native device on FCM's
 * UNREGISTERED -- on iOS, where the plugin observes the SDK. On ANDROID the deletion is
 * handed over unobserved and a failed RPC is silent and unretried, so there the DELETE
 * stays the only half whose outcome the app can see.
 *
 * WHAT WAITS UNDER IT, exactly. On native: the Preferences read, the removal and the
 * DELETE. `deleteToken()` is dispatched and NOT awaited, so it is not on this clock at
 * all. On web: `subscription.unsubscribe()` and the DELETE, side by side -- so a push
 * service slow to confirm the unsubscribe does hold Log Out. "(offline, typically)" was
 * only ever true of native `deleteToken()`: Chrome's `unsubscribe()` resolves offline
 * after deleting local state, and Firefox's can reject offline while still deleting the
 * local record. The number decides how long Log Out waits, not whether pushes keep
 * coming.
 *
 * LEGACY VAPID ONLY on web. Under `VITE_ONESIGNAL_PUSH_OWNER` the teardown finds nothing
 * to end, because `useLegacyWebPushTeardown` has already removed that subscription, and
 * delivery stop rests on `OneSignal.logout()` in `useOneSignalIdentity` (#1223). No
 * environment sets that flag today, and the OneSignal teardown needs the backend's
 * `onesignal_send_enabled` as a second gate.
 *
 * The unbounded await that motivated `boundedCleanup` in the first place is
 * closed at its source instead -- `navigator.serviceWorker.ready` now has a
 * ceiling inside `unregisterPushToken` -- so on a real platform what is left under this
 * number is genuinely the work. It is still a ceiling and not a guarantee of settlement:
 * the PR's own "never settles" rows run under it.
 */
export const PUSH_UNREGISTER_TIMEOUT_MS = 5000;

/**
 * Run a best-effort cleanup step, but never let it be the reason sign-out does
 * not happen.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: this exists because of a
 * dead Log Out button, and the argument that hid it is worth keeping. The
 * `await clearContinuationOnSignOut()` below carried a comment reasoning that
 * the helper "swallows its own failures and resolves `undefined` on every
 * path, so there is nothing here to catch". That is true, and it is a
 * statement about REJECTION, which is not the same as a statement about
 * SETTLEMENT. The chain underneath is `clearContinuations` ->
 * `serialised(...)` -> a module-global promise queue -> on native an
 * `await import('@capacitor/preferences')` and a `Preferences.remove`. Nothing
 * on that path has a timeout, and an operation issued earlier can hold the
 * queue indefinitely.
 *
 * What that cost, traced: `executeSignOut` stopped at that await, so
 * `logtoSignOut` was never called -- the visitor stayed signed in with no
 * redirect and no error. Worse, `signOut` assigns the in-flight promise to
 * `pendingSignOutPromiseRef` and clears it in a `.finally`; a promise that
 * never settles never runs its `.finally`, so the early return at the top of
 * `signOut` handed every later tap the same forever-pending promise. Log Out
 * was dead for the life of the document and only a reload cleared it. Web
 * cannot reach this (localStorage is synchronous); native can.
 *
 * Bounding the wait is what makes `.finally` reachable again, so the pending
 * ref is cleared on every outcome by construction rather than by a second
 * mechanism. Applied to `unregisterPushToken` as well, which the same trace
 * showed has the identical exposure for the identical reason.
 *
 * The step is NOT cancelled on timeout -- there is nothing to cancel and it
 * may still complete -- it is merely stopped from blocking. A step that is
 * abandoned here logs at `warn`, because a cleanup that routinely times out is
 * a defect somebody should see rather than a silent degradation.
 */
const boundedCleanup = async (
  step: () => Promise<unknown>,
  label: string,
  timeoutMs: number = SIGN_OUT_CLEANUP_TIMEOUT_MS,
): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      // `.then(() => undefined)` so a step resolving a value cannot be mistaken
      // for the timeout arm, and a rejection still reaches the catch below.
      step().then(() => undefined),
      new Promise<void>(resolve => {
        timer = setTimeout(() => {
          logger.warn(
            `[signOut] ${label} did not settle within ${timeoutMs}ms; continuing with sign-out`,
          );
          resolve();
        }, timeoutMs);
      }),
    ]);
  } catch (error) {
    // Every caller is best-effort, and this is the layer that guarantees it:
    // a step that starts rejecting in future must not become a sign-out that
    // throws out of `executeSignOut`.
    logger.error(`[signOut] ${label} failed; continuing with sign-out:`, error);
  } finally {
    clearTimeout(timer);
  }
};

// =============================================================================
// HOOK OPTIONS
// =============================================================================

export interface UseSignInOutOptions {
  /** Whether user is authenticated with Logto */
  logtoIsAuthenticated: boolean;
  /** Logto SDK signIn, as AuthProvider wraps it: clears the "Signed in as ..."
   * cue first unless the second argument keeps it. */
  logtoSignIn: (options: LogtoSignInOptions, cue?: { keepSignedInCue?: boolean }) => Promise<void>;
  /** React Router navigate, used to route web CTAs through the /auth door
   * when `featureFlags.magicLinkAuth` is on (see `signIn` below). */
  navigate: NavigateFunction;
  /** Logto SDK signOut function */
  logtoSignOut: (redirectUri?: string) => Promise<void>;
  /** TanStack Query client for cache clearing */
  queryClient: QueryClient;
  /** Analytics tracking function */
  trackEvent: (event: string, properties?: Record<string, unknown>) => void;
  /** Zustand store clearCachedAuth */
  clearCachedAuth: () => void;
  /** Clear user from React Query cache */
  clearUser: () => void;
  /** Zustand store setAuthenticated */
  setAuthenticated: (isAuthenticated: boolean) => void;
  /** Zustand store setCurrentUser */
  setCurrentUser: (user: UserProfileResponse | null) => void;
  /** Zustand store setError */
  setError: (error: string | null) => void;
  /** Refetch user from backend */
  refetchUser: () => Promise<unknown>;
  /** Open draft confirmation dialog */
  openDraftDialog: () => void;
  /** Refs for managing pending sign-out promise (writable) */
  pendingSignOutPromiseRef: { current: Promise<void> | null };
  signOutResolverRef: { current: (() => void) | null };
  signOutRejectRef: { current: ((reason?: unknown) => void) | null };
  /** Ref for tracking dialog close intent (writable) */
  dialogCloseIntentRef: { current: 'confirm' | 'cancel' | null };
}

export interface UseSignInOutReturn {
  /** Initiate sign-in flow. firstScreen defaults to 'register' (see the
   * `SignInOptions` interface in authTypes.ts, which is the single source of
   * truth for this shape, for the app-wide audit note and for onSignInSettled's
   * contract). */
  signIn: (options?: SignInOptions) => void;
  /** Initiate sign-out flow (may show draft confirmation dialog) */
  signOut: () => Promise<void>;
  /** Execute sign-out without draft check */
  executeSignOut: () => Promise<void>;
  /** Reset pending sign-out state */
  resetPendingSignOut: () => void;
  /** Refresh user data from backend */
  refreshUser: () => Promise<void>;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

export function useSignInOut(options: UseSignInOutOptions): UseSignInOutReturn {
  const {
    logtoIsAuthenticated,
    logtoSignIn,
    navigate,
    logtoSignOut,
    queryClient,
    trackEvent,
    clearCachedAuth,
    clearUser,
    setAuthenticated,
    setCurrentUser,
    setError,
    refetchUser,
    openDraftDialog,
    pendingSignOutPromiseRef,
    signOutResolverRef,
    signOutRejectRef,
    dialogCloseIntentRef,
  } = options;

  // AIDEV-NOTE [MEETBALL magic-link]: `navigate` is held in a ref and kept out of
  // signIn's dep array. react-router 6.30 memoises useNavigate on
  // `locationPathname`, so the function gets a NEW identity on every client-side
  // navigation; with it in the deps, `signIn` churned identity on every
  // navigation, which churned AuthProvider's `memoizedValue`, which re-rendered
  // every `useAuth()` consumer in the app. AuthProvider now hands us
  // `useStableNavigate()` instead, which never churns, so that specific trigger
  // is closed at the source and this is the second line of defence rather than
  // the fix -- the same shape as `openDraftDialog` there. It stays because this
  // hook takes `navigate` from its caller: nothing in the type stops a future
  // caller (or a test) from passing a fresh function every render, and the cost
  // of that landing here is a re-render of all 82 useAuth() consumers.
  // Assigned during render, so a callback captured on an earlier render still
  // routes through the CURRENT navigate.
  // The render-phase assignment is deliberate and is why the ref is live rather
  // than one render stale: an effect-based sync would leave a callback invoked
  // between render and commit pointing at the previous navigate.
  const navigateRef = useRef(navigate);
  // eslint-disable-next-line react-hooks/refs -- see above; navigate is not read during render, only stored
  navigateRef.current = navigate;

  /**
   * Reset pending sign-out state
   */
  const resetPendingSignOut = useCallback(() => {
    pendingSignOutPromiseRef.current = null;
    signOutResolverRef.current = null;
    signOutRejectRef.current = null;
    dialogCloseIntentRef.current = null;
  }, [dialogCloseIntentRef, pendingSignOutPromiseRef, signOutRejectRef, signOutResolverRef]);

  /**
   * Execute sign-out without draft check
   *
   * Calls Logto's signOut with homepage as post_logout_redirect_uri.
   * This properly invalidates the server-side session and redirects to homepage.
   *
   * AIDEV-NOTE: The post_logout_redirect_uri must be registered in Logto console.
   * URIs "/", "/home", "/profile", "/signup" must be configured manually per deployment.
   *
   * Flow:
   * 1. Clear app state (non-Logto storage) - preserves Logto tokens for SDK
   * 2. logtoSignOut() redirects to Logto, which invalidates server session and clears SDK storage
   * 3. Only if logtoSignOut throws: clear ALL storage (including Logto) and redirect manually
   */
  const executeSignOut = useCallback(async (): Promise<void> => {
    // THE FIRST STATEMENT, deliberately. Everything below this line runs while
    // the Logto session is still valid: the push DELETE needs its bearer, and
    // on native the adapter stays authenticated for the whole system-browser
    // round trip. For that whole window the current-user query would otherwise
    // refetch `/users/me` and hand the leaving user straight back -- see
    // `AuthState.isSigningOut` and the read in `useCurrentUser`.
    //
    // Deliberately NOT read by `getTokenForApi` or `isSessionBlocked`: the
    // steps below still have to be able to authenticate. And deliberately not
    // part of `isFullyAuthenticated`, so the UI still flips at
    // `clearAuthState` rather than one statement earlier.
    useAuthStore.getState().setSigningOut(true);

    // AND CANCEL WHAT IS ALREADY IN FLIGHT (Copilot thread rv5405548963). The latch
    // closes the door on a `/users/me` that has not STARTED -- the queryFn reads it live
    // and the `enabled` term stops it being scheduled -- but it says nothing about a
    // request already awaiting its response. A full-sync refetch from resuming after
    // idle can easily be open when Log Out is tapped, and when it lands react-query
    // writes the profile and every observer runs: `useAuthAnalytics` re-identifies the
    // leaving user and `wasAuthenticated` and `cachedUserInfo` are persisted again,
    // AFTER `clearCachedAuth` below has cleared them. `clearAuthState` clears neither.
    // Cancelling makes react-query discard a response that arrives from here on.
    void queryClient.cancelQueries({ queryKey: currentUserKey() });

    // Notify other tabs that we're signing out
    CrossTabAuthSync.getInstance().authStateChanged(false, { isUserInitiated: true });

    clearCachedAuth();

    // Clear app state but preserve Logto tokens for SDK to use during signOut
    try {
      clearNonLogtoStorage();
    } catch (storageError) {
      logger.error('Failed to clear non-Logto storage during logout:', storageError);
    }
    // clearNonLogtoStorage() sweeps localStorage (the web continuation mirror
    // included, by construction), but never reaches native Preferences -- same
    // gap `clearAuthIntent()` closes for its own mirror one call ABOVE --
    // `clearAuthIntent` is nested inside `clearNonLogtoStorage`, which is the
    // call immediately preceding this comment, not a later one (id 4074245279).
    // Called
    // here, on the MAIN path, because `logtoSignOut` resolves normally on
    // native (see the AIDEV-NOTE lower in this function); the catch branch
    // below only runs when the SDK throws, which is not the common native
    // sign-out at all.
    //
    // AWAITED, not `void`-ed (id 4056609625). On native the removal is an
    // async Capacitor Preferences call, and fire-and-forget put it in a race
    // with `logtoSignOut` below -- so sign-out could complete with the
    // `requestSecret` still on the device, which is the one thing this call
    // exists to prevent.
    //
    // BOUNDED, because the previous sentence here was that awaiting "cannot
    // break sign-out: the helper swallows its own failures and resolves
    // `undefined` on every path". That reasons about rejection and says
    // nothing about settlement, and the path underneath -- a module-global
    // serial queue, then a dynamic import and a Preferences call, with no
    // timeout anywhere -- can simply not answer. See `boundedCleanup`: an
    // unbounded await here left the visitor signed in AND made Log Out dead
    // for the life of the document.
    //
    // STARTED HERE, AWAITED AT `logtoSignOut` (id 4074244990). The ordering
    // constraint the paragraph above states is against `logtoSignOut` and
    // nothing else, and four independent steps sit between the two -- the
    // attribution clear, the draft-store clear, the push-token unregister and
    // `clearAuthState`. Awaiting on this line put every one of them behind a
    // Capacitor Preferences round trip on native for no reason the code gives.
    // The promise is created here so the removal is already in flight while
    // those run, and the one thing that must not overtake it still cannot.
    const continuationCleared = boundedCleanup(clearContinuationOnSignOut, 'continuation clear');

    // THE NATIVE PENDING-QUESTION CLAIM, on the same seam for the same reason (review 5330705557,
    // R12): both Preferences keys, the marker and the entries. `settlePendingQuestionClaim` KEEPS
    // them on a transient failure, so an attempted claim can still be here, and the next account
    // to sign in on this device would present it: the entries as a loss for this account, a bare
    // marker as the device-token limb, which has no email condition and publishes this account's
    // rows under the next one. Sign-out is when a device changes hands, so the claim ends here.
    // Native only: on the web it lives in the tab keys and the mirror, which the sweep above
    // takes.
    const claimCleared = isNativePlatform()
      ? boundedCleanup(clearNativePendingQuestionClaim, 'pending question claim clear')
      : Promise.resolve();

    // Consume the durable referral attribution on logout so a shared-browser sign-out
    // cannot leak this identity's attribution to whoever signs up next. Lives in the
    // post-veto execute path only - a cancelled sign-out (draft dialog dismissed) never
    // reaches here, so it keeps the attribution. clearAttribution self-guards (never
    // throws), so it needs no try/catch of its own.
    clearAttribution();

    // Clear question draft store
    try {
      useCreateQuestionDraftStore.getState().clearDraft();
    } catch (draftError) {
      logger.error('Failed to clear question draft store during logout:', draftError);
    }

    // End this device's push registration before clearing auth state: the
    // device's own subscription / FCM token first, then the backend DELETE,
    // which still needs the session (best-effort -- don't block logout if it
    // fails).
    //
    // Bounded for the same reason as the continuation clear above: "best
    // effort" has to mean bounded, or a step that never answers is the same
    // dead Log Out button by a different route. On its OWN ceiling, though, and
    // not the 2s local-storage one -- see `PUSH_UNREGISTER_TIMEOUT_MS`. The
    // bound never CANCELS the step (there is nothing to cancel), so a DELETE
    // that settles a moment late still tidies the backend; on web it is also
    // sent with `keepalive`, so ONCE IT IS IN FLIGHT the navigation `logtoSignOut`
    // performs below does not abort it. Not before: axios runs its async request
    // interceptor first, and that awaits the bearer, which can mean a Logto token
    // refresh behind a cross-tab lock. A navigation inside that phase loses the
    // request, because there is no request yet. Whether the device keeps receiving
    // pushes no longer rides on this wait at all (#1173): its own teardown is started
    // before the DELETE and does not wait for it (legacy VAPID on web; see
    // `PUSH_UNREGISTER_TIMEOUT_MS`).
    await boundedCleanup(unregisterPushToken, 'push token unregister', PUSH_UNREGISTER_TIMEOUT_MS);

    // AIDEV-NOTE: Issue #567 - Shared cleanup runs cancelQueries() before clear()
    // to prevent stale writes into a cleared cache. clearAllTokens is a no-op
    // here because Logto SDK clears its own tokens during the signOut redirect
    // below; passing a no-op keeps the shared ordering contract intact.
    await clearAuthState({
      queryClient,
      setAuthenticated,
      setCurrentUser,
      clearUser,
      clearAllTokens: async () => {
        /* Handled by logtoSignOut() redirect */
      },
    });
    setError(null);

    // Redirect to homepage after logout - this URI is registered in Logto
    // On native, uses deep link scheme so system browser redirects back to app (AR-6 fix)
    const postLogoutRedirectUri = getPostLogoutRedirectUri();

    logger.info('Logout initiated, redirecting to Logto end session', {
      postLogoutRedirectUri,
      timestamp: new Date().toISOString(),
    });

    // The one ordering constraint, collected here rather than at the top: the
    // `requestSecret` and the claim must be off the device before the sign-out
    // that can unload it. `boundedCleanup` owns both ceilings, so this cannot
    // hang, and the two were started together, so it waits for one ceiling.
    await Promise.all([continuationCleared, claimCleared]);

    try {
      // This redirects to Logto's end session endpoint, which then redirects to postLogoutRedirectUri
      // Logto SDK handles its own token cleanup during this flow
      await logtoSignOut(postLogoutRedirectUri);
      // On web: this line is unreachable (logtoSignOut redirects the browser).
      // On native: signOut resolves after the system browser flow completes.
      // Navigate to home since we cleared all auth state above.
      if (isNativePlatform()) {
        globalThis.location.replace('/home');
      }
    } catch (signOutError) {
      // Only reaches here if logtoSignOut throws without redirecting
      logger.error('Logto signOut failed, falling back to manual redirect:', signOutError);

      // Since SDK didn't complete, manually clear all storage including Logto tokens
      try {
        clearUserStorage();
      } catch (cleanupError) {
        logger.error('Failed to clear user storage during fallback logout:', cleanupError);
      }
      // Redundant with the main-path call above on any run that reaches this
      // far, but the clear is idempotent and cheap, and this branch is the
      // last chance if that earlier call's promise never settled.
      //
      // Awaited for the same reason, and here the race is not subtle: the
      // `location.replace` two lines down unloads the document, so a pending
      // Preferences removal is simply cancelled. Bounded for the same reason
      // as the main-path call: an unbounded await here would keep the visitor
      // on a page that has already cleared its own auth state and never reach
      // the redirect. The claim gets the same last chance, beside it.
      await Promise.all([
        boundedCleanup(clearContinuationOnSignOut, 'continuation clear (fallback)'),
        isNativePlatform()
          ? boundedCleanup(
              clearNativePendingQuestionClaim,
              'pending question claim clear (fallback)',
            )
          : undefined,
      ]);

      // Full page navigation (not SPA routing) ensures clean app state after manual cleanup
      globalThis.location.replace('/');
    }
  }, [
    clearCachedAuth,
    clearUser,
    logtoSignOut,
    queryClient,
    setAuthenticated,
    setCurrentUser,
    setError,
  ]);

  /**
   * Enhanced sign in with callback URL
   * @param options.loginHint - Optional email to prefill in the signup form (used for guest question submission)
   */
  const signIn = useCallback(
    (signInOptions?: SignInOptions) => {
      // Release the sign-out latch. A document normally does not survive its
      // own sign-out, but one can: on web @logto/react wraps `signOut` in a
      // proxy that swallows every error without rethrowing, so a sign-out that
      // fails before it navigates returns normally and leaves this document
      // standing with the latch still set. Without this the next Sign in tap
      // would authenticate and then never load a profile.
      //
      // ONLY WHEN LOGTO IS ALSO SIGNED OUT. In the failed-web-sign-out case the adapter
      // is STILL authenticated and still holds the old bearer, and the door route is a
      // client-side navigation that does not replace the document. Releasing there would
      // re-enable the current-user query against the leaving session, reload that
      // profile, and let the push initializers register it again -- the whole shape this
      // latch exists to stop, reached from the other end. Leaving it set costs nothing:
      // every sign-in route out of that document ends in a full Logto redirect, and the
      // new document starts with the latch false because it is not persisted.
      if (!logtoIsAuthenticated) {
        useAuthStore.getState().setSigningOut(false);
      }

      const callbackUrl = getAuthCallbackUrl();
      const hasGlobal = typeof globalThis !== 'undefined';
      const { location } = hasGlobal ? globalThis : { location: undefined };

      const currentPath = location?.pathname ?? '/';
      const currentSearch = location?.search ?? '';
      const currentHash = location?.hash ?? '';
      // AIDEV-NOTE: Use /home as landing since it's now guest-friendly
      const intendedPath = currentPath === '/' ? '/home' : currentPath;

      // Check if user has an auth intent or pending question claim.
      // If so, always store redirect path to return them to complete the action.
      //
      // AIDEV-NOTE [MEETBALL magic-link]: through the leaf reader, which was a raw
      // `sessionStorage.getItem` behind a `typeof` guard. That guard covers the
      // property being ABSENT and not the access THROWING, which is the failure mode
      // that actually happens: iOS Safari with "Block All Cookies" and hardened mail
      // WebViews throw on the property access itself. A throw here escaped
      // `signIn`, so the visitor's tap on a sign-in button did nothing at all -- in
      // exactly the browsers this flow is most often opened in. One reader, one
      // guard: `hasPendingQuestionClaimInTab` swallows it and answers false.
      //
      // THE UNION, not the bare flag: since the flag is armed only for a row nothing can name, a
      // post-secrets guest holds entries and no flag, and reading the flag alone would stop
      // recognising this screen's own flow -- restoring the redirect to the empty create form
      // that the note below exists to prevent.
      const isPendingQuestionFlow = hasPendingQuestionClaimInTab();
      const hasIntent = hasAuthIntent();

      // AIDEV-NOTE [MEETBALL magic-link]: the marker no longer stands for "this tab
      // is STARTING the guest-question flow". `useCallbackRedirect` KEEPS it on a
      // RETRYABLE claim failure (STEP 2), so it now outlives the callback that used
      // to retire it either way, and a stale one was suppressing a LATER, unrelated
      // sign-in's destination: one 502 and the visitor on /events/evt-1 who tapped
      // Sign in landed on /home, silently, until the next sign-out sweep
      // (`clearUserStorage`). Keyed on the SCREEN the flow's own sign-in is raised
      // from instead -- `useCreateQuestionSubmit` arms the flag and calls signIn()
      // one statement later, and /create-question is its only guest-reachable mount
      // (/questions/:slugOrId/edit is behind ProtectedRoute) -- which cannot go
      // stale. The flow's own two other sign-in sites, AuthDoor and /auth/link, are
      // already covered by `!isAuthRoute` below.
      const isStartingPendingQuestionFlow =
        isPendingQuestionFlow && normalizeRoutePath(intendedPath) === routes.question.create();

      // AIDEV-NOTE: For pending question claim, redirect is handled after claiming:
      // STEP 2 falls through to STEP 3, whose own default is /home. That is the
      // destination this suppression chooses -- deliberately NOT the form the visitor
      // just submitted, which would return them to an empty create-question screen
      // instead of to the question they came back to see posted. The suppression only
      // stops a NEW stash; an OLDER one is cleared by the ask itself, at the create site
      // (`useCreateQuestionSubmit`), which also covers the partial-auth exit that never
      // reaches this function (review 5330705557, R03).
      // AIDEV-NOTE [MEETBALL magic-link]: the `!isAuthRoute` term is NOT
      // redundant with shouldStoreRedirectPath's own auth-route exclusion. `||`
      // short-circuits, so an intent-carrying flow never called that function at
      // all -- and the intent-carrying flows are precisely the ones that reach
      // signIn FROM /auth ("Use a code instead", every social button on the
      // door). Without this, tapping "Use a code instead" overwrote the good
      // destination with '/auth' itself and the callback returned the visitor to
      // a door they had already come through. An auth route is never a valid
      // post-auth destination, intent or no intent.
      const shouldPersist =
        !isStartingPendingQuestionFlow &&
        !isAuthRoute(intendedPath) &&
        (hasIntent || shouldStoreRedirectPath(intendedPath));

      if (shouldPersist) {
        storeRedirectPath(intendedPath, currentSearch, currentHash);
      }

      logger.debug('[signIn] Initiating auth', {
        intendedPath,
        hasAuthIntent: hasIntent,
        shouldPersist,
      });

      // AIDEV-NOTE [MEETBALL magic-link]: `shouldRouteToDoor` governs every
      // explicit-sign-in CTA (this callback) and useAuthNavigation's auto-guard.
      // It used to be two hand-written expressions differing by the bypassDoor
      // term, each carrying a CHANGE ONE, CHANGE THE OTHER note; it is now one
      // leaf module (@lib/magicLink/doorRouting), which is where the exemption
      // rules and the silent-recovery carve-out are written down.
      //
      // It must NEVER govern useInvalidGrantHandler's silent-recovery
      // logtoSignIn calls (Prompt.None at runWebAttempts' "Attempt 1", plus the
      // full-login fallback and the native attempt) -- those exist to refresh an
      // existing session without user interaction, and rerouting them through
      // the /auth door would break token-refresh recovery entirely.
      // useInvalidGrantHandler.ts calls logtoSignIn directly and is untouched by
      // this flag on purpose.
      const routeToDoor = shouldRouteToDoor({ bypassDoor: signInOptions?.bypassDoor });

      trackEvent(ANALYTICS_EVENTS.auth_initiated, {
        entrypoint: routeToDoor ? 'auth_door' : 'explicit_sign_in',
        intended_path: intendedPath,
        has_intended_action: hasIntent,
        login_hint_present: Boolean(signInOptions?.loginHint),
        action_type: signInOptions?.actionType ?? null,
        ...(signInOptions?.contextType ? { context_type: signInOptions.contextType } : {}),
      });

      // AIDEV-NOTE: Removed postRedirectUri to prevent Logto from redirecting before
      // our Callback.tsx can check if user needs to complete SignupFlow.
      // We rely on sessionStorage redirect path which Callback.tsx reads after
      // verifying the user has a profile.

      // Mark that user explicitly initiated sign-in so we broadcast to other tabs after callback
      userInitiatedSignInSlot.write(true);

      if (routeToDoor) {
        // Flag on, web, and not a call from the door itself (bypassDoor):
        // send the user to our own /auth screen instead of redirecting to
        // Logto's hosted UI. Intended-path storage above already ran identically
        // to the legacy path.
        //
        // AIDEV-NOTE [MEETBALL magic-link]: the cross-tab MIRRORS used to be
        // written here. They now live in AuthDoor's mount effect, because the
        // door's own signIn calls all pass `bypassDoor: true` and so never
        // reached this branch -- which meant a previous visitor's intent and
        // destination survived into an unrelated later sign-in. See the note on
        // that effect. Nothing else about this branch changed.
        //
        // AIDEV-NOTE [MEETBALL magic-link]: loginHint rides in router STATE,
        // not a query param. A `?email=` query param would reopen the exact
        // Amplitude/history/Referer leak that authLinkParams.ts's stash exists
        // to close (autocapture attaches the full URL to its page-view event
        // before AuthDoor's own scrub effect can run). Router state never
        // touches the URL. This is what keeps loginHint-carrying callers
        // (InviteGate, InviteAcceptDrawer, useCreateQuestionSubmit) prefilling
        // the door's email field instead of silently dropping the hint.
        // `replace`, not push: the CTA's origin page has no business sitting
        // behind the door in history. On the auto-guard's twin (useAuthNavigation)
        // the pushed entry is an outright dead end -- Back restores a protected
        // route client-side with no effect re-run to re-fire the guard -- and
        // Callback.tsx navigates to this same destination with `replace` too.
        navigateRef.current(AUTH_DOOR_PATH, {
          replace: true,
          ...(signInOptions?.loginHint ? { state: { email: signInOptions.loginHint } } : {}),
        });
        // NOTE the ordering difference from the legacy path below, where this
        // runs after logtoSignIn settles: here the door hop is a client-side
        // navigation with nothing to await, so the callback fires synchronously
        // right after it, and it resolved. Documented on
        // SignInOptions.onSignInSettled in authTypes.ts; every current caller is
        // order-insensitive teardown.
        signInOptions?.onSignInSettled?.({ kind: 'resolved' });
        return;
      }

      // AIDEV-NOTE: loginHint prefills email in Logto signup form for guest question submission flow.
      // On native, loginHint is injected into the OIDC URL via adapter.navigate wrapper
      // in NativeLogtoAdapterProvider (workaround for @logto/capacitor not forwarding it).
      // On web, signIn resolves as the page redirect starts; on native it resolves or
      // rejects -- the rejection arm below prevents an unhandled one.
      const sdkOptions: LogtoSignInOptions = {
        redirectUri: callbackUrl,
        firstScreen: signInOptions?.firstScreen ?? 'register',
        ...(signInOptions?.loginHint ? { loginHint: signInOptions.loginHint } : {}),
        // AIDEV-NOTE [MEETBALL magic-link]: rides through as the `identifier`
        // authorization param. Logto's oidc-provider only recognises the keys in
        // `ExtraParamsKey`, and only `one_time_token`, `login_hint` and
        // `identifier` are copied onto the hosted-UI URL, so this is one of the
        // few channels that reaches the fork at all.
        ...(signInOptions?.identifiers?.length ? { identifiers: signInOptions.identifiers } : {}),
        ...(signInOptions?.extraParams ? { extraParams: signInOptions.extraParams } : {}),
        ...(signInOptions?.prompt ? { prompt: signInOptions.prompt } : {}),
        ...(signInOptions?.directSignIn ? { directSignIn: signInOptions.directSignIn } : {}),
      };
      // `keepSignedInCue` is ours, not Logto's, so it travels beside the SDK
      // options rather than inside them; see SignInOptions.keepSignedInCue.
      const attempt = signInOptions?.keepSignedInCue
        ? logtoSignIn(sdkOptions, { keepSignedInCue: true })
        : logtoSignIn(sdkOptions);
      // Both arms settle, so the callback gets the outcome (#1119); semantics on
      // SignInOptions.onSignInSettled.
      void attempt.then(
        () => {
          signInOptions?.onSignInSettled?.({ kind: 'resolved' });
        },
        (error: unknown) => {
          if (!isUserCancellation(error)) {
            logger.error('Sign-in failed:', error);
          }
          signInOptions?.onSignInSettled?.({ kind: 'rejected', error });
        },
      );
    },
    // signInOptions is a call-time argument, not a closure dependency, so the
    // new callback adds no dep here. `navigate` is deliberately absent too: it
    // lives in navigateRef (see the note at the top of the hook).
    [logtoIsAuthenticated, logtoSignIn, trackEvent],
  );

  /**
   * Enhanced sign out with redirect URL and cleanup
   * Shows draft confirmation dialog if user has unsaved draft
   */
  const signOut = useCallback(async (): Promise<void> => {
    if (pendingSignOutPromiseRef.current) {
      return pendingSignOutPromiseRef.current;
    }

    if (hasDraftInStorage()) {
      const confirmationPromise = new Promise<void>((resolve, reject) => {
        signOutResolverRef.current = resolve;
        signOutRejectRef.current = reject;
        openDraftDialog();
      });

      const wrappedPromise = confirmationPromise.finally(() => {
        resetPendingSignOut();
      });
      pendingSignOutPromiseRef.current = wrappedPromise;

      return wrappedPromise;
    }

    const immediateSignOutPromise = Promise.resolve().then(() => executeSignOut());

    const wrappedImmediate = immediateSignOutPromise.finally(() => {
      resetPendingSignOut();
    });
    pendingSignOutPromiseRef.current = wrappedImmediate;

    return wrappedImmediate;
  }, [
    executeSignOut,
    openDraftDialog,
    pendingSignOutPromiseRef,
    resetPendingSignOut,
    signOutRejectRef,
    signOutResolverRef,
  ]);

  /**
   * Refresh user data from backend
   */
  const refreshUser = useCallback(async (): Promise<void> => {
    if (!logtoIsAuthenticated) {
      return;
    }

    try {
      await refetchUser();
    } catch (error) {
      logger.error('Failed to refresh user:', error);
    }
  }, [logtoIsAuthenticated, refetchUser]);

  return {
    signIn,
    signOut,
    executeSignOut,
    resetPendingSignOut,
    refreshUser,
  };
}

// Re-export for convenience
export { LogoutCancelledError };
